import gql from 'graphql-tag';

// QUERIES
const allPressReleasesQuery = gql`
  query allPressReleases {
    allPressReleases {
      _id
      publishedOn
      isSelfPublished
      title
      mediaUrl
      pressUrl
      providers
    }
  }
`;

const cartItemCountQuery = gql`
  query cartItemCount($customerIdForAdmin: String) {
    cartItemCount(customerIdForAdmin: $customerIdForAdmin) {
      _id
      numberOfItems
      hasSubscription
    }
  }
`;

const cartInfo = gql`
  mutation cartInfo(
    $fetchCartWithPendingPurchase: Boolean
    $customerIdForAdmin: String
    $referralCode: String
  ) {
    cartInfo(
      fetchCartWithPendingPurchase: $fetchCartWithPendingPurchase
      customerIdForAdmin: $customerIdForAdmin
      referralCode: $referralCode
    ) {
      _id
      hasPointsApplied
      purchaser {
        guestCheckoutEmail
        email
        phoneNumber
        name {
          first
          last
        }
        isSubscribedToNewsletter
        isAgreedToTermsAndConditions
      }
      babyBirthday
      babyGender
      subscriberDiscountAmount
      giftMessage
      shippingInsurance {
        cost
      }
      isCartDigitalOnly
      productsAndDetails {
        _id
        isAddedCouponProduct
        product {
          _id
          name
          brand
          actualCost
          originalCost
          showVariationsAsMultipleItems
          isDropShip
          isComposite
          sku
          urlName
          tags {
            _id
            description
            isInternal
            bitsyParent {
              _id
              description
            }
            bumpChildren {
              _id
              description
            }
          }
          media {
            color
            url
            variation
          }
          quantityDetails {
            variation
            color
            quantity
          }
          ownPromoData {
            isSubPromo
            isProductPromo
            isEverythingPromo
            isPostPurchasePromo
            isPreCheckoutPromo
            isVitaminPromo
            expiresOn
            validOn
            isAddOn
            isRecurringAddOn
            cost
            header
            subHeader
            buttonText
            promoDescription
          }
        }
        variation
        color
        quantity
        amountToCharge
        promoItem
      }
      coupon {
        _id
        code
        discountPercentage
        discountAmount
        freeShipping
      }
      giftCards {
        code
        originalAmount
        amountRemaining
      }
      subscription {
        isGift
        plan {
          _id
          title
          costWithCoupon
          actualCost
          originalCost
          actualCostAsShipping
          type
          months
          couponToAutoPopulate {
            _id
          }
          bulletPoints {
            point1
          }
        }
        babyBirthday
      }
      shippingAddress {
        name {
          first
          last
        }
        phoneNumber
        streetAddress
        aptDetails
        city
        state
        zipCode
        country
      }
      dateToEmail
      firstBoxShippingAddress {
        name {
          first
          last
        }
        phoneNumber
        streetAddress
        aptDetails
        city
        state
        zipCode
        country
      }
      firstBoxShippingMethod
      isBrownBoxSelected
      firstBoxShipping {
        _id
        cost
        isInternational
        timeToShipInBusinessDays
        title
      }
      subCosts {
        subTotal
        giftCardDiscount
        couponDiscount
        shipping
        taxesAndFees
        overallTotal
        potentialGiftCardDiscount
        potentialTaxesAndFees
        potentialOverallTotal
        applicablePointsWithDiscount
        pointsDiscount
        shippingInsuranceCost
      }
    }
  }
`;

const getSubscriptionQuery = gql`
  query getSubscription($customerIdForAdmin: String, $subscriptionId: String!) {
    getSubscription(
      customerIdForAdmin: $customerIdForAdmin
      subscriptionId: $subscriptionId
    ) {
      _id
      status
      isPurchaser
      giftEmailStatus
      isGift
      shippingInsurance {
        cost
      }
      plan {
        _id
        provider
        months
        title
        costWithCoupon
        actualCost
        originalCost
        actualCostAsShipping
        type
      }
      babyGender
      babyBirthday
      pregnancyStatus
      recipientData {
        name {
          first
          last
        }
        email
        dateToEmail
      }
      firstBoxShippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      firstBoxShippingMethod
      shippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      billingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      paymentMethod {
        brand
        last4
        expMonth
        expYear
      }
      purchaser {
        email
      }
    }
  }
`;

const subscriptionInProgressQuery = gql`
  query subscriptionInProgress(
    $customerIdForAdmin: String!
    $isGift: Boolean!
  ) {
    subscriptionInProgress(
      customerIdForAdmin: $customerIdForAdmin
      isGift: $isGift
    ) {
      _id
      status
      giftEmailStatus
      isGift
      plan {
        _id
        provider
        months
        title
        costWithCoupon
        actualCost
        originalCost
        actualCostAsShipping
      }
      babyGender
      babyBirthday
      recipientData {
        name {
          first
          last
        }
        email
        dateToEmail
      }
      firstBoxShippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      firstBoxShippingMethod
      firstBoxShipping {
        _id
        cost
        isInternational
        timeToShipInBusinessDays
        title
      }
      shippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      billingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      paymentMethod {
        brand
        last4
        expMonth
        expYear
      }
      coupon {
        code
        discountPercentage
        discountAmount
      }
      isBrownBoxSelected
      giftCards {
        code
        originalAmount
        amountRemaining
      }
    }
  }
`;

const productQuery = gql`
  query productInfo(
    $productId: String
    $productUrlName: String
    $customerIdForAdmin: String
  ) {
    productInfo(
      productId: $productId
      productUrlName: $productUrlName
      customerIdForAdmin: $customerIdForAdmin
    ) {
      _id
      name
      description
      metaDescription
      descriptionComponents
      sku
      ingredients
      actualCost
      originalCost
      urlName
      totalQuantity
      variationName
      averageRating
      isDropShip
      isComposite
      tagsToDisplay
      tagToRenderUnder
      freeReturns
      brand
      shouldShowAddToNextBoxForProduct
      media {
        color
        url
        variation
        altTag
      }
      tags {
        ${/* _id here will result in null tags */ ''}
        description
        isInternal
        bitsyParent {
          description
        }
        bumpParent {
          description
        }
      }
      colorsDictionary {
        permanentName
        displayName
        hex
        media
      }
      quantityDictionaryAsString
      createdAt
      subscriptionPlan {
        _id
        provider
        months
        title
        orderToShow
        costWithCoupon
        actualCost
        originalCost
        tagType
        isHidden
        minimumMonths
        actualCostAsShipping
        couponToAutoPopulate {
          _id
          code
          discountPercentage
          discountAmount
          freeShipping
          addedProducts {
            _id
            name
            description
            descriptionComponents
            ingredients
            colors
            variations
            originalCost
            actualCost
            urlName
            brand
            media {
              url
            }
          }
        }
        bulletPoints {
          point1
          point2
          point3
          point4
        }
      }
      discounted
      error {
        key
        value
      }
    }
  }
`;

const getProductsQuery = gql`
  query getProducts(
    $sortFilter: String
    $searchText: String
    $tag: String
    $tagDescription: String
    $minCost: Float
    $maxCost: Float
    $offset: Int
    $limit: Int
    $provider: String
    $customerIdForAdmin: String
  ) {
    getProducts(
      sortFilter: $sortFilter
      searchText: $searchText
      tag: $tag
      tagDescription: $tagDescription
      minCost: $minCost
      maxCost: $maxCost
      offset: $offset
      limit: $limit
      provider: $provider
      customerIdForAdmin: $customerIdForAdmin
    ) {
      _id
      name
      isActive
      description
      ingredients
      originalCost
      actualCost
      urlName
      averageRating
      quantityDictionaryAsString
      totalQuantity
      variationName
      showColorsAsMultipleItems
      showVariationsAsMultipleItems
      isDropShip
      isComposite
      brand
      sku
      media {
        color
        url
        variation
        altTag
      }
      tags {
        _id
        description
        isInternal
        bitsyParent {
          _id
          description
        }
        bumpParent {
          _id
          description
        }
      }
      ownPromoData {
        isSubPromo
        isProductPromo
        isEverythingPromo
        isPostPurchasePromo
        isPreCheckoutPromo
        isVitaminPromo
        isAddOn
        isRecurringAddOn
        cost
        header
        subHeader
        buttonText
        promoDescription
      }
      subscriptionPlan {
        _id
        provider
        months
        title
        orderToShow
        costWithCoupon
        actualCost
        originalCost
        tagType
        isHidden
        minimumMonths
        actualCostAsShipping
        couponToAutoPopulate {
          _id
          code
          discountPercentage
          discountAmount
          freeShipping
          addedProducts {
            _id
            name
            description
            descriptionComponents
            ingredients
            colors
            variations
            originalCost
            actualCost
            urlName
            brand
            media {
              url
            }
          }
        }
        bulletPoints {
          point1
          point2
          point3
          point4
        }
      }
      discounted
      totalNumberOfProducts
      __typename @skip(if: true)
    }
  }
`;

const availablePointsQuery = gql`
  query availablePointsQuery($customerIdForAdmin: String) {
    availablePoints(customerIdForAdmin: $customerIdForAdmin) {
      _id
      availablePoints
    }
  }
`;

const userInfoQuery = gql`
  query userInfoQuery($customerIdForAdmin: String) {
    userInfo(customerIdForAdmin: $customerIdForAdmin) {
      _id
      email
      phoneNumber
      accountType
      activeCompanies
      subscriberDiscountAmount
      name {
        first
        last
      }
      availablePoints
      points {
        pointsDifference
        expirationDate
        usedOrRefundedDate
      }
      giftCards {
        code
        originalAmount
        amountRemaining
        providers
      }
      addresses {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      creditCards {
        brand
        last4
        expMonth
        expYear
      }
      isVerified
      referralCode
      purchases {
        _id
        confirmationCode
        provider
        paidStatus
        shippingInsurance {
          cost
        }
        costs {
          subTotal
          pointsDiscount
          discount
          shipping
          taxesAndFees
          overallTotal
        }
        productsAndDetails {
          _id
          product {
            _id
            name
            brand
            urlName
            isActive
            isTrash
            isOnBumpBoxes
            isOnBitsyBoxes
            media {
              color
              url
            }
            tags {
              _id
              description
              isInternal
              bitsyParent {
                _id
                description
              }
              bumpChildren {
                _id
                description
              }
            }
            quantityDetails {
              variation
              color
              quantity
            }
          }
          variation
          color
          quantity
          amountToCharge
        }
        subscription {
          isGift
          lastOrderShipStationTrackingNumber
          lastOrderShipStationCarrierCode
          renewOn
          pregnancyStatus
          shippingInsurance {
            cost
          }
          recipientData {
            name {
              first
              last
            }
            email
          }
          plan {
            _id
            title
            type
            months
          }
        }
        refunds {
          returnMethod
          giftCard {
            code
          }
          amountRefunded
          dateRefunded
        }
        order {
          _id
          status
          shipStationTrackingNumber
          shipStationCarrierCode
          shippingAddress {
            country
          }
          billingAddress {
            city
            state
            zipCode
          }
        }
        createdAt
      }
      subscriptions {
        _id
        giftEmailStatus
        status
        confirmationCode
        lastOrderShipStationTrackingNumber
        lastOrderShipStationCarrierCode
        provider
        renewOn
        pregnancyStatus
        scheduledCancel
        numberOfOrders
        credits
        extensions
        shippingInsurance {
          cost
        }
        recurringAddOns {
          amountToCharge
          product {
            _id
            name
            isActive
            description
            ingredients
            originalCost
            actualCost
            urlName
            averageRating
            totalQuantity
            isDropShip
            isComposite
            tagsToDisplay
            tagToRenderUnder
            discounted
            brand
            ownPromoData {
              isSubPromo
              isProductPromo
              isEverythingPromo
              isPostPurchasePromo
              isPreCheckoutPromo
              isVitaminPromo
              expiresOn
              validOn
              isAddOn
              isRecurringAddOn
              cost
              header
              subHeader
              buttonText
              promoDescription
            }
            media {
              color
              url
              altTag
            }
            tags {
              _id
              description
              isInternal
              bitsyParent {
                _id
                description
              }
              bumpChildren {
                _id
                description
              }
            }
          }
        }
        plan {
          _id
          provider
          months
          title
          type
          costWithCoupon
          actualCost
          originalCost
          minimumMonths
        }
        babyGender
        babyBirthday
        monthsToSkip
        isSkipCurrentMonth
        currentOrderMonth
        recipientData {
          name {
            first
            last
          }
          email
          dateToEmail
        }
        firstBoxShippingAddress {
          name {
            first
            last
          }
          country
          streetAddress
          aptDetails
          city
          state
          zipCode
        }
        firstBoxShippingMethod
        shippingAddress {
          name {
            first
            last
          }
          country
          streetAddress
          aptDetails
          city
          state
          zipCode
        }
        billingAddress {
          name {
            first
            last
          }
          country
          streetAddress
          aptDetails
          city
          state
          zipCode
        }
        paymentMethod {
          brand
          last4
          expMonth
          expYear
        }
        canMakePayment
        usedCoupons {
          _id
        }
        addOnsWithDetails {
          quantity
          replacementItem
          color
          variation
          amountToCharge
          product {
            _id
            name
            isActive
            description
            ingredients
            originalCost
            actualCost
            urlName
            averageRating
            isDropShip
            isComposite
            tagsToDisplay
            tagToRenderUnder
            discounted
            brand
            tags {
              _id
              description
              isInternal
            }
            media {
              color
              url
              altTag
            }
          }
        }
      }
      reviews {
        _id
        product {
          _id
          name
          isOnBumpBoxes
          isOnBitsyBoxes
          media {
            color
            url
          }
        }
        stars
        description
        isActive
        createdAt
        updatedAt
      }
      registry {
        _id
        subscription {
          _id
        }
      }
      subscriptionsWithFailedPurchase
    }
  }
`;

const allShippingOptionsQuery = gql`
  query allShippingOptions {
    allShippingOptions {
      _id
      title
      cost
      isInternational
      timeToShipInBusinessDays
    }
  }
`;

const allPlansQuery = gql`
  query allPlans {
    allPlans {
      _id
      provider
      months
      title
      orderToShow
      costWithCoupon
      actualCost
      originalCost
      tagType
      isHidden
      minimumMonths
      bulletPoints {
        point1
        point2
        point3
        point4
      }
    }
  }
`;

const plansForProviderQuery = gql`
  query plansForProvider($provider: String!) {
    plansForProvider(provider: $provider) {
      _id
      provider
      months
      title
      type
      orderToShow
      costWithCoupon
      actualCost
      originalCost
      tagType
      isHidden
      minimumMonths
      selectSubMessage
      circleMessage
      isDefault
      couponToAutoPopulate {
        _id
        code
        discountPercentage
        discountAmount
        freeShipping
        addedProducts {
          _id
          name
          description
          descriptionComponents
          ingredients
          colors
          variations
          originalCost
          actualCost
          urlName
          brand
          media {
            url
          }
        }
      }
      bulletPoints {
        point1
        point2
        point3
        point4
      }
      highlights {
        order
        text
        className
      }
    }
  }
`;

const orderInfoQuery = gql`
  query orderInfo(
    $orderConfirmationCode: String!
    $customerIdForAdmin: String
  ) {
    orderInfo(
      orderConfirmationCode: $orderConfirmationCode
      customerIdForAdmin: $customerIdForAdmin
    ) {
      _id
      confirmationCode
      shipStationTrackingNumber
      shipStationCarrierCode
      billingAddress {
        name {
          first
        }
      }
      purchaser {
        _id
        referralCode
      }
    }
  }
`;

const purchaseInfoQuery = gql`
  query purchaseInfo($purchaseId: String!, $customerIdForAdmin: String) {
    purchaseInfo(
      purchaseId: $purchaseId
      customerIdForAdmin: $customerIdForAdmin
    ) {
      _id
      shippingInsurance {
        cost
      }
      subscription {
        recurringAddOns {
          amountToCharge
        }
        plan {
          _id
          provider
          months
          title
          costWithCoupon
          actualCost
          originalCost
          type
        }
        shippingAddress {
          name {
            first
            last
          }
          country
          streetAddress
          aptDetails
          city
          state
          zipCode
        }
        billingAddress {
          name {
            first
            last
          }
          country
          streetAddress
          aptDetails
          city
          state
          zipCode
        }
        recipientData {
          name {
            first
            last
          }
          email
          dateToEmail
        }
        paymentMethod {
          brand
          last4
          expMonth
          expYear
        }
        firstBoxShippingMethod
        isGift
        isBrownBoxSelected
      }
      order {
        _id
        shippingAddress {
          name {
            first
            last
          }
          country
          streetAddress
          aptDetails
          city
          state
          zipCode
        }
        billingAddress {
          name {
            first
            last
          }
          country
          streetAddress
          aptDetails
          city
          state
          zipCode
        }
        recipientData {
          name {
            first
            last
          }
          email
          dateToEmail
        }
        paymentMethod {
          brand
          last4
          expMonth
          expYear
        }
        firstBoxShippingMethod
        isGift
        actualCost
        isBrownBoxSelected
        provider
        months
        title
      }
      costs {
        subTotal
        pointsDiscount
        discount
        shipping
        taxesAndFees
        overallTotal
        shippingInsuranceCost
      }
      confirmationCode
      purchaser {
        _id
        isTemporary
        referralCode
      }
      paidStatus
    }
  }
`;

const getCurrentShopTag = gql`
  query getCurrentShopTag($description: String!) {
    getCurrentShopTag(description: $description) {
      description
      bitsyParent {
        description
      }
      bumpParent {
        description
      }
      bitsyChildren {
        description
      }
      bumpChildren {
        description
      }
    }
  }
`;

const getPromoProductsForCart = gql`
  query getPromoProductsForCart($tempCart: String) {
    getPromoProductsForCart(tempCart: $tempCart) {
      _id
      name
      description
      descriptionComponents
      ingredients
      actualCost
      originalCost
      urlName
      totalQuantity
      variationName
      averageRating
      isDropShip
      isComposite
      freeReturns
      brand
      media {
        color
        url
        variation
      }
      colorsDictionary {
        permanentName
        displayName
        hex
        media
      }
      quantityDictionaryAsString
      ownPromoData {
        isSubPromo
        isProductPromo
        isEverythingPromo
        isPostPurchasePromo
        isPreCheckoutPromo
        isVitaminPromo
        expiresOn
        validOn
        isAddOn
        isRecurringAddOn
        cost
        header
        subHeader
        buttonText
        promoDescription
      }
      error {
        key
        value
      }
    }
  }
`;

const getPromoProductsForPostPurchase = gql`
  query getPromoProductsForPostPurchase {
    getPromoProductsForPostPurchase {
      _id
      name
      description
      descriptionComponents
      ingredients
      actualCost
      originalCost
      urlName
      totalQuantity
      variationName
      averageRating
      isDropShip
      isComposite
      freeReturns
      discounted
      brand
      media {
        color
        url
        variation
      }
      colorsDictionary {
        permanentName
        displayName
        hex
        media
      }
      quantityDictionaryAsString
      ownPromoData {
        isSubPromo
        isProductPromo
        isEverythingPromo
        isPostPurchasePromo
        isPreCheckoutPromo
        isVitaminPromo
        expiresOn
        validOn
        isAddOn
        isRecurringAddOn
        cost
        header
        subHeader
        buttonText
        promoDescription
      }
      error {
        key
        value
      }
    }
  }
`;

const afterPurchaseOffers = gql`
  query afterPurchaseOffers(
    $orderConfirmationCode: String
    $customerIdForAdmin: String
  ) {
    afterPurchaseOffers(
      orderConfirmationCode: $orderConfirmationCode
      customerIdForAdmin: $customerIdForAdmin
    ) {
      _id
      name
      imageUrl
      description1
      description2
      agency
      isSelected
      internalOfferSurvey
      ctaButtonText
      ctaButtonSelectedText
      isBumpLifeOptIn
      trustedform_cert_url
      policyTextAndUrls {
        text
        link
        styling
      }
      error {
        key
        value
      }
      surveyQuestions {
        questionId
        questionText
        type
        responses {
          displayText
          responseCode
        }
      }
      recurringAddOnProduct {
        _id
      }
      surveyImageUrl
      directUrl
    }
  }
`;

// may need to edit this -- not sure what info we need yet
const getBreastPumpProducts = gql`
  query getBreastPumpProducts {
    getBreastPumpProducts {
      _id
      name
      description
      descriptionComponents
      ingredients
      actualCost
      originalCost
      urlName
      totalQuantity
      variationName
      averageRating
      isDropShip
      isComposite
      freeReturns
      media {
        color
        url
        variation
      }
      colorsDictionary {
        permanentName
        displayName
        hex
        media
      }
      quantityDictionaryAsString
      ownPromoData {
        isSubPromo
        isProductPromo
        isEverythingPromo
        isPostPurchasePromo
        isPreCheckoutPromo
        isVitaminPromo
        expiresOn
        validOn
        isAddOn
        isRecurringAddOn
        cost
        header
        subHeader
        buttonText
        promoDescription
      }
      error {
        key
        value
      }
    }
  }
`;

const getCompanyByCode = gql`
  query getCompanyByCode($companyCode: String!) {
    getCompanyByCode(companyCode: $companyCode) {
      _id
      companyCode
      name
      isActive
      paymentType
      shippingOptions {
        _id
        title
        cost
        isInternational
        timeToShipInBusinessDays
      }
      purchases {
        provider
        confirmationCode
        createdAt
        numberOfPaymentAttempts
        stripeDeclineCode
        paidStatus
        file
        paymentType
      }
      billingAddress {
        name {
          first
          last
        }
        country
        state
        zipCode
        city
        streetAddress
        aptDetails
      }
      error {
        key
        value
      }
    }
  }
`;

const getCorporateProducts = gql`
  query getCorporateProducts($companyCode: String!) {
    getCorporateProducts(companyCode: $companyCode) {
      _id
      name
      description
      ingredients
      originalCost
      actualCost
      urlName
      averageRating
      quantityDictionaryAsString
      totalQuantity
      variationName
      isDropShip
      isComposite
      media {
        color
        url
        variation
        altTag
      }
      tags {
        _id
        description
        isInternal
        bitsyParent {
          _id
          description
        }
        bumpParent {
          _id
          description
        }
      }
    }
  }
`;

const validateCompanySignUpUrl = gql`
  query validateCompanySignUpUrl($signUpCode: String!) {
    validateCompanySignUpUrl(signUpCode: $signUpCode) {
      _id
      name
      companyCode
      error {
        key
        value
      }
    }
  }
`;

const addEmailToMasterList = gql`
  query addEmailToMasterList($email: String!, $provider: String!) {
    addEmailToMasterList(email: $email, provider: $provider) {
      _id
    }
  }
`;

const sendDueDateCalculatorKlaviyoEvents = gql`
  query sendDueDateCalculatorKlaviyoEvents(
    $email: String!
    $provider: String!
    $dueDate: String!
    $lastPeriodDate: String!
    $conceptionDate: String!
    $testDate: String!
    $firstHeartBeat: String!
    $weeksPregnant: String!
    $babysFirstMove: String!
    $endTrimester1: String!
    $endTrimester2: String!
  ) {
    sendDueDateCalculatorKlaviyoEvents(
      email: $email
      provider: $provider
      dueDate: $dueDate
      lastPeriodDate: $lastPeriodDate
      conceptionDate: $conceptionDate
      testDate: $testDate
      firstHeartBeat: $firstHeartBeat
      weeksPregnant: $weeksPregnant
      babysFirstMove: $babysFirstMove
      endTrimester1: $endTrimester1
      endTrimester2: $endTrimester2
    ) {
      _id
    }
  }
`;

const addEmailToBreastPumpMasterList = gql`
  query addEmailToBreastPumpMasterList($email: String!, $provider: String!) {
    addEmailToBreastPumpMasterList(email: $email, provider: $provider) {
      _id
    }
  }
`;

const addEmailToGiveawayList = gql`
  query addEmailToGiveawayList(
    $email: String!
    $provider: String!
    $streetAddress: String
    $aptDetails: String
    $city: String
    $state: String
    $zipCode: String
    $country: String
    $dueDate: String
    $giveawayName: String
  ) {
    addEmailToGiveawayList(
      email: $email
      provider: $provider
      streetAddress: $streetAddress
      aptDetails: $aptDetails
      city: $city
      state: $state
      zipCode: $zipCode
      country: $country
      dueDate: $dueDate
      giveawayName: $giveawayName
    ) {
      _id
    }
  }
`;

const sendLifeInsuranceQuoteEmail = gql`
  query sendLifeInsuranceQuoteEmail(
    $email: String!
    $provider: String!
    $name: String
    $streetAddress: String
    $aptDetails: String
    $city: String
    $state: String
    $zipCode: String
    $country: String
    $phoneNumber: String
  ) {
    sendLifeInsuranceQuoteEmail(
      email: $email
      provider: $provider
      name: $name
      streetAddress: $streetAddress
      aptDetails: $aptDetails
      city: $city
      state: $state
      zipCode: $zipCode
      country: $country
      phoneNumber: $phoneNumber
    ) {
      _id
      email
    }
  }
`;

const getSubscriptionAddOnProducts = gql`
  query getSubscriptionAddOnProducts(
    $customerIdForAdmin: String
    $subscriptionId: String!
  ) {
    getSubscriptionAddOnProducts(
      customerIdForAdmin: $customerIdForAdmin
      subscriptionId: $subscriptionId
    ) {
      _id
      name
      description
      descriptionComponents
      sku
      ingredients
      actualCost
      originalCost
      urlName
      totalQuantity
      variationName
      averageRating
      isDropShip
      isComposite
      tagsToDisplay
      tagToRenderUnder
      freeReturns
      brand
      shouldShowAddToNextBoxForProduct
      media {
        color
        url
        variation
        altTag
      }
      tags {
        description
        isInternal
        bitsyParent {
          _id
          description
        }
        bumpChildren {
          _id
          description
        }
      }
      colorsDictionary {
        permanentName
        displayName
        hex
        media
      }
      quantityDictionaryAsString
      createdAt
      ownPromoData {
        isSubPromo
        isProductPromo
        isEverythingPromo
        isPostPurchasePromo
        isPreCheckoutPromo
        isVitaminPromo
        expiresOn
        validOn
        isAddOn
        isRecurringAddOn
        cost
        header
        subHeader
        buttonText
        promoDescription
      }
      subscriptionPlan {
        _id
        provider
        months
        title
        orderToShow
        costWithCoupon
        actualCost
        originalCost
        tagType
        isHidden
        minimumMonths
        actualCostAsShipping
        couponToAutoPopulate {
          _id
          code
          discountPercentage
          discountAmount
          freeShipping
          addedProducts {
            _id
            name
            description
            descriptionComponents
            ingredients
            colors
            variations
            originalCost
            actualCost
            urlName
            brand
            media {
              url
            }
          }
        }
        bulletPoints {
          point1
          point2
          point3
          point4
        }
      }
      discounted
      error {
        key
        value
      }
    }
  }
`;

const getSubscriptionAddOnProductsForLandingPage = gql`
  query getSubscriptionAddOnProductsForLandingPage {
    getSubscriptionAddOnProductsForLandingPage {
      _id
      name
      description
      descriptionComponents
      sku
      ingredients
      actualCost
      originalCost
      urlName
      totalQuantity
      variationName
      averageRating
      isDropShip
      isComposite
      tagsToDisplay
      tagToRenderUnder
      freeReturns
      brand
      shouldShowAddToNextBoxForProduct
      media {
        color
        url
        variation
        altTag
      }
      tags {
        _id
        description
        isInternal
        bitsyParent {
          _id
          description
        }
        bumpChildren {
          _id
          description
        }
      }
      colorsDictionary {
        permanentName
        displayName
        hex
        media
      }
      quantityDictionaryAsString
      createdAt
      reviews {
        _id
        stars
        description
        isActive
        updatedAt
        createdAt
        reviewer {
          name {
            first
            last
          }
        }
      }
      subscriptionPlan {
        _id
        provider
        months
        title
        orderToShow
        costWithCoupon
        actualCost
        originalCost
        tagType
        isHidden
        minimumMonths
        actualCostAsShipping
        couponToAutoPopulate {
          _id
          code
          discountPercentage
          discountAmount
          freeShipping
          addedProducts {
            _id
            name
            description
            descriptionComponents
            ingredients
            colors
            variations
            originalCost
            actualCost
            urlName
            brand
            media {
              url
            }
          }
        }
        bulletPoints {
          point1
          point2
          point3
          point4
        }
      }
      discounted
      error {
        key
        value
      }
    }
  }
`;

const getRegistryInfo = gql`
  query getRegistryInfo($registryId: String, $shareCode: String) {
    getRegistryInfo(registryId: $registryId, shareCode: $shareCode) {
      _id
      name {
        first
        last
      }
      subscription {
        babyGender
        babyBirthday
      }
      sponsors {
        name {
          first
          last
        }
        email
        numOfBoxesSponsored
        giftMessage
        thankYouCreatedAt
        isAnonymous
        createdAt
      }
      displayGender
      displayDueDate
      numOfBoxesSponsored
      daysToGo
      registryProgress
      shareCode
    }
  }
`;

const getRegistryInfoForOwner = gql`
  query getRegistryInfoForOwner($customerIdForAdmin: String) {
    getRegistryInfoForOwner(customerIdForAdmin: $customerIdForAdmin) {
      _id
      name {
        first
        last
      }
      subscription {
        _id
        babyGender
        babyBirthday
        shippingAddress {
          name {
            first
            last
          }
          streetAddress
          aptDetails
          city
          state
          zipCode
          country
        }
      }
      sponsors {
        _id
        name {
          first
          last
        }
        email
        numOfBoxesSponsored
        giftMessage
        thankYouCreatedAt
        isAnonymous
        createdAt
      }
      displayGender
      displayDueDate
      numOfBoxesSponsored
      daysToGo
      registryProgress
      shareCode
    }
  }
`;

const searchForRegistries = gql`
  query searchForRegistries($firstName: String, $lastName: String) {
    searchForRegistries(firstName: $firstName, lastName: $lastName) {
      _id
      name {
        first
        last
      }
      subscription {
        babyGender
        babyBirthday
        shippingAddress {
          state
        }
      }
      displayGender
      displayDueDate
      shareCode
    }
  }
`;

const getRegistryProduct = gql`
  query getRegistryProduct {
    getRegistryProduct {
      _id
      name
      description
      actualCost
      originalCost
      media {
        color
        url
        variation
      }
      error {
        key
        value
      }
    }
  }
`;

const validateRegistryAddress = gql`
  query validateRegistryAddress(
    $streetAddress: String!
    $aptDetails: String
    $city: String!
    $state: String!
    $zipCode: String!
    $country: String!
  ) {
    validateRegistryAddress(
      streetAddress: $streetAddress
      aptDetails: $aptDetails
      city: $city
      state: $state
      zipCode: $zipCode
      country: $country
    ) {
      suggestedAddresses {
        streetAddress
        city
        state
        zipCode
      }
    }
  }
`;

// MUTATIONS
const addItemToCart = gql`
  mutation addItemToCart(
    $customerIdForAdmin: String
    $planId: String
    $product: String
    $variation: String
    $color: String
    $quantity: Int
    $promoItem: Boolean
    $fetchCartWithPendingPurchase: Boolean
  ) {
    addItemToCart(
      customerIdForAdmin: $customerIdForAdmin
      planId: $planId
      product: $product
      variation: $variation
      color: $color
      quantity: $quantity
      promoItem: $promoItem
      fetchCartWithPendingPurchase: $fetchCartWithPendingPurchase
    ) {
      _id
      numberOfItems
      hasSubscription
      firstBoxShipping {
        _id
        cost
        isInternational
        timeToShipInBusinessDays
        title
      }
      productsAndDetails {
        _id
        product {
          _id
          name
          brand
          urlName
          sku
          media {
            color
            url
          }
          quantityDetails {
            variation
            color
            quantity
          }
          ownPromoData {
            isSubPromo
            isProductPromo
            isEverythingPromo
            isPostPurchasePromo
            isPreCheckoutPromo
            isVitaminPromo
            expiresOn
            validOn
            isAddOn
            isRecurringAddOn
            cost
            header
            subHeader
            buttonText
            promoDescription
          }
        }
        variation
        color
        quantity
        amountToCharge
        promoItem
      }
      subscription {
        isGift
        plan {
          _id
          title
          costWithCoupon
          actualCost
          originalCost
          actualCostAsShipping
        }
        babyBirthday
      }
      coupon {
        _id
        code
        discountPercentage
        discountAmount
        freeShipping
      }
      subCosts {
        subTotal
        giftCardDiscount
        couponDiscount
        shipping
        taxesAndFees
        overallTotal
        potentialGiftCardDiscount
        potentialTaxesAndFees
        potentialOverallTotal
        applicablePointsWithDiscount
        pointsDiscount
      }
      error {
        key
        value
      }
    }
  }
`;

const signInUser = gql`
  mutation signInUser(
    $provider: String!
    $tempCart: String
    $email: String!
    $password: String!
    $isSigningInAsAdmin: Boolean
    $cartId: String
  ) {
    signInUser(
      provider: $provider
      tempCart: $tempCart
      email: $email
      password: $password
      isSigningInAsAdmin: $isSigningInAsAdmin
      cartId: $cartId
    ) {
      _id
      isSubscribedToNewsletter
      email
      name {
        first
        last
      }
      accountType
      lastPasswordChange
      isVerified
      token
      referralCode
      points {
        pointsDifference
      }
      error {
        key
        value
      }
    }
  }
`;

const signOutUser = gql`
  mutation signOutUser {
    signOutUser {
      _id
    }
  }
`;

const signUpUser = gql`
  mutation signUpUser(
    $provider: String!
    $tempCart: String
    $firstName: String
    $lastName: String
    $email: String!
    $phoneNumber: String!
    $password: String!
    $isSubscribedToNewsletter: Boolean!
    $isAgreedToTermsAndConditions: Boolean!
    $cartId: String
    $doNotSendPasswordEmail: Boolean
    $companyCode: String
    $dueDate: String
    $attentiveVisitorId: String
    $isOptedInSMS: Boolean
  ) {
    signUpUser(
      provider: $provider
      tempCart: $tempCart
      firstName: $firstName
      lastName: $lastName
      email: $email
      phoneNumber: $phoneNumber
      password: $password
      isSubscribedToNewsletter: $isSubscribedToNewsletter
      isAgreedToTermsAndConditions: $isAgreedToTermsAndConditions
      cartId: $cartId
      doNotSendPasswordEmail: $doNotSendPasswordEmail
      companyCode: $companyCode
      dueDate: $dueDate
      attentiveVisitorId: $attentiveVisitorId
      isOptedInSMS: $isOptedInSMS
    ) {
      _id
      isSubscribedToNewsletter
      email
      name {
        first
        last
      }
      isVerified
      token
      referralCode
      error {
        key
        value
      }
    }
  }
`;

const refreshUserToken = gql`
  query refreshUserToken($provider: String!) {
    refreshUserToken(provider: $provider) {
      _id
      token
      name {
        first
        last
      }
    }
  }
`;

const confirmUserPassword = gql`
  mutation confirmUserPassword(
    $provider: String!
    $email: String!
    $password: String!
  ) {
    confirmUserPassword(
      provider: $provider
      email: $email
      password: $password
    ) {
      _id
      email
      error {
        key
        value
      }
    }
  }
`;

const getEmptyUser = gql`
  mutation getEmptyUser($provider: String!, $tempCart: String) {
    getEmptyUser(provider: $provider, tempCart: $tempCart) {
      _id
      token
      error {
        key
        value
      }
    }
  }
`;

const addAllShippingData = gql`
  mutation addAllShippingData(
    $customerIdForAdmin: String
    $shippingFirstName: String!
    $shippingLastName: String!
    $shippingStreetAddress: String!
    $shippingAptDetails: String
    $shippingCity: String!
    $shippingState: String!
    $shippingZipCode: String!
    $shippingCountry: String!
    $isBrownBoxSelected: Boolean!
    $firstBoxShipping: String!
    $isVerified: Boolean
    $skipAddressValidation: Boolean!
  ) {
    addAllShippingData(
      customerIdForAdmin: $customerIdForAdmin
      shippingFirstName: $shippingFirstName
      shippingLastName: $shippingLastName
      shippingStreetAddress: $shippingStreetAddress
      shippingAptDetails: $shippingAptDetails
      shippingCity: $shippingCity
      shippingState: $shippingState
      shippingZipCode: $shippingZipCode
      shippingCountry: $shippingCountry
      isBrownBoxSelected: $isBrownBoxSelected
      firstBoxShipping: $firstBoxShipping
      isVerified: $isVerified
      skipAddressValidation: $skipAddressValidation
    ) {
      _id
      shippingAddress {
        name {
          first
          last
        }
        phoneNumber
        streetAddress
        aptDetails
        city
        state
        zipCode
        country
      }
      isBrownBoxSelected
      firstBoxShipping {
        _id
        cost
        isInternational
        timeToShipInBusinessDays
        title
      }
      subCosts {
        subTotal
        giftCardDiscount
        couponDiscount
        shipping
        taxesAndFees
        overallTotal
        potentialGiftCardDiscount
        potentialTaxesAndFees
        potentialOverallTotal
        applicablePointsWithDiscount
        pointsDiscount
        shippingInsuranceCost
      }
      suggestedAddresses {
        streetAddress
        city
        state
        zipCode
      }
      error {
        key
        value
      }
    }
  }
`;

const addSubscriptionDetails = gql`
  mutation addSubscriptionDetails(
    $customerIdForAdmin: String
    $giftMessage: String
    $babyGender: String
    $day: String
    $month: String
    $year: String
    $pregnancyStatus: String
    $dueDateUnknown: Boolean
  ) {
    addSubscriptionDetails(
      customerIdForAdmin: $customerIdForAdmin
      giftMessage: $giftMessage
      babyGender: $babyGender
      day: $day
      month: $month
      year: $year
      pregnancyStatus: $pregnancyStatus
      dueDateUnknown: $dueDateUnknown
    ) {
      _id
      babyBirthday
      babyGender
      giftMessage
    }
  }
`;

const reactivateSubscription = gql`
  mutation reactivateSubscription($subscriptionId: String!) {
    reactivateSubscription(subscriptionId: $subscriptionId) {
      _id
      status
      isSkipCurrentMonth
      specialRequirementsOnLabel
      currentOrderMonth
      confirmationCode
      plan {
        _id
        provider
        months
        title
        actualCost
        originalCost
      }
      isGift
      nextPaymentDate
      lastPaymentDate
      createdAt
      babyGender
      babyBirthday
      recipientData {
        name {
          first
          last
        }
        email
        dateToEmail
      }
      orderHistory {
        _id
        createdAt
        shippingAddress {
          name {
            first
            last
          }
          country
          streetAddress
          aptDetails
          city
          state
          zipCode
        }
        shippingType {
          title
          cost
        }
        isBrownBoxSelected
        confirmationCode
      }
      firstBoxShippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      firstBoxShippingMethod
      shippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      billingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      paymentMethod {
        cardId
        customerId
        brand
        last4
        expMonth
        expYear
      }
    }
  }
`;

const extendSubscription = gql`
  mutation extendSubscription($subscriptionId: String!) {
    extendSubscription(subscriptionId: $subscriptionId) {
      _id
      status
      isSkipCurrentMonth
      specialRequirementsOnLabel
      currentOrderMonth
      confirmationCode
      plan {
        _id
        provider
        months
        title
        actualCost
        originalCost
      }
      isGift
      nextPaymentDate
      lastPaymentDate
      createdAt
      babyGender
      babyBirthday
      recipientData {
        name {
          first
          last
        }
        email
        dateToEmail
      }
      orderHistory {
        _id
        createdAt
        shippingAddress {
          name {
            first
            last
          }
          country
          streetAddress
          aptDetails
          city
          state
          zipCode
        }
        shippingType {
          title
          cost
        }
        isBrownBoxSelected
        confirmationCode
      }
      firstBoxShippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      firstBoxShippingMethod
      shippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      billingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      paymentMethod {
        cardId
        customerId
        brand
        last4
        expMonth
        expYear
      }
    }
  }
`;

const addGuestCheckoutData = gql`
  mutation addGuestCheckoutData(
    $customerIdForAdmin: String
    $guestCheckoutEmail: String!
    $guestCheckoutPhoneNumber: String!
    $isAgreedToTermsAndConditions: Boolean!
    $isSubscribedToNewsletter: Boolean!
  ) {
    addGuestCheckoutData(
      customerIdForAdmin: $customerIdForAdmin
      guestCheckoutEmail: $guestCheckoutEmail
      guestCheckoutPhoneNumber: $guestCheckoutPhoneNumber
      isAgreedToTermsAndConditions: $isAgreedToTermsAndConditions
      isSubscribedToNewsletter: $isSubscribedToNewsletter
    ) {
      _id
      guestCheckoutEmail
      email
      phoneNumber
      name {
        first
        last
      }
      isSubscribedToNewsletter
      isAgreedToTermsAndConditions
    }
  }
`;

const purchaseCart = gql`
  mutation purchaseCart(
    $customerIdForAdmin: String
    $paymentToken: String!
    $billingFirstName: String!
    $billingLastName: String!
    $billingStreetAddress: String
    $billingAptDetails: String
    $billingCity: String
    $billingState: String
    $billingZipCode: String
    $billingCountry: String
    $shippingFirstName: String!
    $shippingLastName: String!
    $shippingStreetAddress: String!
    $shippingAptDetails: String
    $shippingCity: String!
    $shippingState: String!
    $shippingZipCode: String!
    $shippingCountry: String!
    $giftMessage: String
    $shippingOption: String
    $birthDay: String
    $birthMonth: String
    $birthYear: String
    $gender: String
    $couponCode: String
    $giftCards: String
    $paymentType: String
    $isPendingPayment: Boolean
    $companyCode: String
    $companyPaymentType: String
    $isAnonymous: Boolean
    $registryId: String
    $maxBountyClickId: String
    $referralCode: String
  ) {
    purchaseCart(
      customerIdForAdmin: $customerIdForAdmin
      paymentToken: $paymentToken
      billingFirstName: $billingFirstName
      billingLastName: $billingLastName
      billingStreetAddress: $billingStreetAddress
      billingAptDetails: $billingAptDetails
      billingCity: $billingCity
      billingState: $billingState
      billingZipCode: $billingZipCode
      billingCountry: $billingCountry
      shippingFirstName: $shippingFirstName
      shippingLastName: $shippingLastName
      shippingStreetAddress: $shippingStreetAddress
      shippingAptDetails: $shippingAptDetails
      shippingCity: $shippingCity
      shippingState: $shippingState
      shippingZipCode: $shippingZipCode
      shippingCountry: $shippingCountry
      giftMessage: $giftMessage
      shippingOption: $shippingOption
      birthDay: $birthDay
      birthMonth: $birthMonth
      birthYear: $birthYear
      gender: $gender
      couponCode: $couponCode
      giftCards: $giftCards
      paymentType: $paymentType
      isPendingPayment: $isPendingPayment
      companyCode: $companyCode
      companyPaymentType: $companyPaymentType
      isAnonymous: $isAnonymous
      registryId: $registryId
      maxBountyClickId: $maxBountyClickId
      referralCode: $referralCode
    ) {
      _id
      isBrownBoxSelected
      userPoints
      coupon {
        _id
        code
        discountPercentage
        discountAmount
      }
      giftCards {
        code
        originalAmount
        amountRemaining
      }
      firstBoxShipping {
        _id
        cost
        isInternational
        timeToShipInBusinessDays
        title
      }
      shippingAddress {
        name {
          first
          last
        }
        phoneNumber
        streetAddress
        aptDetails
        city
        state
        zipCode
        country
      }
      billingAddress {
        name {
          first
          last
        }
        streetAddress
        aptDetails
        city
        state
        zipCode
        country
      }
      paymentMethod {
        brand
        last4
        expMonth
        expYear
      }
      subCosts {
        subTotal
        pointsDiscount
        giftCardDiscount
        couponDiscount
        shipping
        taxesAndFees
        overallTotal
        shippingInsuranceCost
      }
      justPurchasedPurchaseId
      firstBoxShippingAddress {
        name {
          first
          last
        }
        streetAddress
        aptDetails
        city
        state
        zipCode
        country
      }
      firstBoxShippingMethod
      productsAndDetails {
        _id
        product {
          _id
          name
          brand
          sku
          urlName
          media {
            url
            color
          }
          ownPromoData {
            isSubPromo
            isProductPromo
            isEverythingPromo
            isPostPurchasePromo
            isPreCheckoutPromo
            isVitaminPromo
            expiresOn
            validOn
            isAddOn
            isRecurringAddOn
            cost
            header
            subHeader
            buttonText
            promoDescription
          }
        }
        variation
        color
        quantity
      }
      subscription {
        _id
        status
        subCosts {
          subTotal
          pointsDiscount
          giftCardDiscount
          couponDiscount
          shipping
          taxesAndFees
          overallTotal
        }
        plan {
          _id
          provider
          months
          title
          costWithCoupon
          actualCost
          originalCost
        }
        recipientData {
          name {
            first
            last
          }
          email
          dateToEmail
        }
        giftMessage
      }
      error {
        key
        value
      }
    }
  }
`;

const addDiscountToCart = gql`
  mutation addDiscountToCart(
    $customerIdForAdmin: String
    $code: String!
    $isCoupon: Boolean
    $isGift: Boolean
  ) {
    addDiscountToCart(
      customerIdForAdmin: $customerIdForAdmin
      code: $code
      isCoupon: $isCoupon
      isGift: $isGift
    ) {
      _id
      numberOfItems
      hasSubscription
      firstBoxShipping {
        _id
        cost
        isInternational
        timeToShipInBusinessDays
        title
      }
      productsAndDetails {
        _id
        product {
          _id
          name
          brand
          actualCost
          media {
            color
            url
          }
          quantityDetails {
            variation
            color
            quantity
          }
          ownPromoData {
            isSubPromo
            isProductPromo
            isEverythingPromo
            isPostPurchasePromo
            isPreCheckoutPromo
            isVitaminPromo
            expiresOn
            validOn
            isAddOn
            isRecurringAddOn
            cost
            header
            subHeader
            buttonText
            promoDescription
          }
        }
        variation
        color
        quantity
        amountToCharge
      }
      subscription {
        isGift
        plan {
          _id
          title
          costWithCoupon
          actualCost
          originalCost
          actualCostAsShipping
        }
        babyBirthday
      }
      subCosts {
        subTotal
        giftCardDiscount
        couponDiscount
        shipping
        taxesAndFees
        overallTotal
        potentialGiftCardDiscount
        potentialTaxesAndFees
        potentialOverallTotal
        applicablePointsWithDiscount
        pointsDiscount
      }
      coupon {
        _id
        code
        discountPercentage
        discountAmount
      }
      giftCards {
        code
        originalAmount
      }
      error {
        key
        value
      }
    }
  }
`;

const removeDiscountFromCart = gql`
  mutation removeDiscountFromCart(
    $customerIdForAdmin: String
    $isCoupon: Boolean
    $giftCardIndex: Int
  ) {
    removeDiscountFromCart(
      customerIdForAdmin: $customerIdForAdmin
      isCoupon: $isCoupon
      giftCardIndex: $giftCardIndex
    ) {
      _id
      numberOfItems
      hasSubscription
      firstBoxShipping {
        _id
        cost
        isInternational
        timeToShipInBusinessDays
        title
      }
      productsAndDetails {
        _id
        product {
          _id
          name
          brand
          actualCost
          media {
            color
            url
          }
          quantityDetails {
            variation
            color
            quantity
          }
          ownPromoData {
            isSubPromo
            isProductPromo
            isEverythingPromo
            isPostPurchasePromo
            isPreCheckoutPromo
            isVitaminPromo
            expiresOn
            validOn
            isAddOn
            isRecurringAddOn
            cost
            header
            subHeader
            buttonText
            promoDescription
          }
        }
        variation
        color
        quantity
        amountToCharge
      }
      subscription {
        isGift
        plan {
          _id
          title
          costWithCoupon
          actualCost
          originalCost
          actualCostAsShipping
        }
        babyBirthday
      }
      subCosts {
        subTotal
        giftCardDiscount
        couponDiscount
        shipping
        taxesAndFees
        overallTotal
        potentialGiftCardDiscount
        potentialTaxesAndFees
        potentialOverallTotal
        applicablePointsWithDiscount
        pointsDiscount
      }
      coupon {
        _id
        code
        discountPercentage
        discountAmount
      }
      giftCards {
        code
        originalAmount
      }
      error {
        key
        value
      }
    }
  }
`;

const editAccountDetails = gql`
  mutation editAccountDetails(
    $provider: String!
    $customerIdForAdmin: String
    $firstName: String!
    $lastName: String!
    $email: String!
    $phoneNumber: String
    $password: String
    $dueDate: String
    $generatePassword: Boolean
    $orderConfirmationCode: String
  ) {
    editAccountDetails(
      provider: $provider
      customerIdForAdmin: $customerIdForAdmin
      firstName: $firstName
      lastName: $lastName
      email: $email
      phoneNumber: $phoneNumber
      password: $password
      dueDate: $dueDate
      generatePassword: $generatePassword
      orderConfirmationCode: $orderConfirmationCode
    ) {
      _id
      isSubscribedToNewsletter
      email
      phoneNumber
      name {
        first
        last
      }
    }
  }
`;

const editShippingAddress = gql`
  mutation editShippingAddress(
    $subscriptionId: String!
    $firstName: String!
    $lastName: String!
    $streetAddress: String!
    $aptDetails: String
    $city: String!
    $state: String!
    $zipCode: String!
    $country: String!
  ) {
    editShippingAddress(
      subscriptionId: $subscriptionId
      firstName: $firstName
      lastName: $lastName
      streetAddress: $streetAddress
      aptDetails: $aptDetails
      city: $city
      state: $state
      zipCode: $zipCode
      country: $country
    ) {
      _id
      status
      plan {
        _id
        provider
        months
        title
        costWithCoupon
        actualCost
        originalCost
      }
      babyGender
      babyBirthday
      recipientData {
        name {
          first
          last
        }
        email
        dateToEmail
      }
      firstBoxShippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      firstBoxShippingMethod
      shippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      billingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      paymentMethod {
        brand
        last4
        expMonth
        expYear
      }
    }
  }
`;

const editPaymentMethod = gql`
  mutation editPaymentMethod(
    $customerIdForAdmin: String
    $subscriptionId: String!
    $paymentToken: String!
    $provider: String
    $firstName: String!
    $lastName: String!
    $streetAddress: String
    $aptDetails: String
    $city: String
    $state: String
    $zipCode: String
    $country: String
  ) {
    editPaymentMethod(
      customerIdForAdmin: $customerIdForAdmin
      subscriptionId: $subscriptionId
      paymentToken: $paymentToken
      provider: $provider
      firstName: $firstName
      lastName: $lastName
      streetAddress: $streetAddress
      aptDetails: $aptDetails
      city: $city
      state: $state
      zipCode: $zipCode
      country: $country
    ) {
      _id
      status
      plan {
        _id
        provider
        months
        title
        costWithCoupon
        actualCost
        originalCost
      }
      babyGender
      babyBirthday
      recipientData {
        name {
          first
          last
        }
        email
        dateToEmail
      }
      firstBoxShippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      firstBoxShippingMethod
      shippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      billingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      paymentMethod {
        brand
        last4
        expMonth
        expYear
      }
    }
  }
`;

const editBabyDetails = gql`
  mutation editBabyDetails(
    $subscriptionId: String!
    $babyGender: String
    $month: String
    $day: String
    $year: String
    $pregnancyStatus: String
  ) {
    editBabyDetails(
      subscriptionId: $subscriptionId
      babyGender: $babyGender
      month: $month
      day: $day
      year: $year
      pregnancyStatus: $pregnancyStatus
    ) {
      _id
      status
      plan {
        _id
        provider
        months
        title
        costWithCoupon
        actualCost
        originalCost
        type
      }
      babyGender
      babyBirthday
      pregnancyStatus
      recipientData {
        name {
          first
          last
        }
        email
        dateToEmail
      }
      firstBoxShippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      firstBoxShippingMethod
      shippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      billingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      paymentMethod {
        brand
        last4
        expMonth
        expYear
      }
    }
  }
`;

const sendForgotPasswordLink = gql`
  mutation sendForgotPasswordLink($email: String!, $provider: String!) {
    sendForgotPasswordLink(email: $email, provider: $provider) {
      email
      error {
        key
        value
      }
    }
  }
`;

const resetPassword = gql`
  mutation resetPassword(
    $resetPasswordCode: String!
    $password: String!
    $provider: String
  ) {
    resetPassword(
      resetPasswordCode: $resetPasswordCode
      password: $password
      provider: $provider
    ) {
      email
      error {
        key
        value
      }
    }
  }
`;

const skipNextMonth = gql`
  mutation skipNextMonth(
    $customerIdForAdmin: String
    $subscriptionId: String!
  ) {
    skipNextMonth(
      customerIdForAdmin: $customerIdForAdmin
      subscriptionId: $subscriptionId
    ) {
      _id
      status
      plan {
        _id
        provider
        months
        title
        costWithCoupon
        actualCost
        originalCost
      }
      monthsToSkip
      babyGender
      babyBirthday
      recipientData {
        name {
          first
          last
        }
        email
        dateToEmail
      }
      firstBoxShippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      firstBoxShippingMethod
      shippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      billingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      paymentMethod {
        brand
        last4
        expMonth
        expYear
      }
    }
  }
`;

const unskipNextMonth = gql`
  mutation unskipNextMonth(
    $customerIdForAdmin: String
    $subscriptionId: String!
  ) {
    unskipNextMonth(
      customerIdForAdmin: $customerIdForAdmin
      subscriptionId: $subscriptionId
    ) {
      _id
      status
      plan {
        _id
        provider
        months
        title
        costWithCoupon
        actualCost
        originalCost
      }
      monthsToSkip
      babyGender
      babyBirthday
      recipientData {
        name {
          first
          last
        }
        email
        dateToEmail
      }
      firstBoxShippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      firstBoxShippingMethod
      shippingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      billingAddress {
        name {
          first
          last
        }
        country
        streetAddress
        aptDetails
        city
        state
        zipCode
      }
      paymentMethod {
        brand
        last4
        expMonth
        expYear
      }
    }
  }
`;

const sendCorporateGiftingContactEmail = gql`
  mutation sendCorporateGiftingContactEmail(
    $aName: String!
    $companyName: String!
    $companyEmail: String!
    $companyPhoneNumber: String!
    $provider: String
    $utmSource: String
  ) {
    sendCorporateGiftingContactEmail(
      aName: $aName
      companyName: $companyName
      companyEmail: $companyEmail
      companyPhoneNumber: $companyPhoneNumber
      provider: $provider
      utmSource: $utmSource
    ) {
      error {
        key
        value
      }
    }
  }
`;

const sendEmailToHelloBitsyBoxesWithMessage = gql`
  mutation sendEmailToHelloBitsyBoxesWithMessage(
    $name: String!
    $email: String!
    $message: String!
    $subject: String
    $provider: String
  ) {
    sendEmailToHelloBitsyBoxesWithMessage(
      name: $name
      email: $email
      message: $message
      subject: $subject
      provider: $provider
    ) {
      error {
        key
        value
      }
    }
  }
`;

const addReview = gql`
  mutation addReview(
    $customerIdForAdmin: String
    $productId: String!
    $stars: Int!
    $description: String!
  ) {
    addReview(
      customerIdForAdmin: $customerIdForAdmin
      productId: $productId
      stars: $stars
      description: $description
    ) {
      _id
      error {
        key
        value
      }
    }
  }
`;

const editReview = gql`
  mutation editReview(
    $customerIdForAdmin: String
    $reviewId: String!
    $stars: Int
    $description: String
  ) {
    editReview(
      customerIdForAdmin: $customerIdForAdmin
      reviewId: $reviewId
      stars: $stars
      description: $description
    ) {
      _id
      stars
      description
      error {
        key
        value
      }
    }
  }
`;

const deleteReview = gql`
  mutation deleteReview($customerIdForAdmin: String, $reviewId: String!) {
    deleteReview(customerIdForAdmin: $customerIdForAdmin, reviewId: $reviewId) {
      _id
      error {
        key
        value
      }
    }
  }
`;

const removeItemFromCart = gql`
  mutation removeItemFromCart(
    $customerIdForAdmin: String
    $productDetailsIdToRemove: String!
  ) {
    removeItemFromCart(
      customerIdForAdmin: $customerIdForAdmin
      productDetailsIdToRemove: $productDetailsIdToRemove
    ) {
      _id
      numberOfItems
      hasSubscription
      firstBoxShipping {
        _id
        cost
        isInternational
        timeToShipInBusinessDays
        title
      }
      productsAndDetails {
        _id
        product {
          _id
          name
          brand
          actualCost
          urlName
          media {
            color
            url
          }
          quantityDetails {
            variation
            color
            quantity
          }
          ownPromoData {
            isSubPromo
            isProductPromo
            isEverythingPromo
            isPostPurchasePromo
            isPreCheckoutPromo
            isVitaminPromo
            expiresOn
            validOn
            isAddOn
            isRecurringAddOn
            cost
            header
            subHeader
            buttonText
            promoDescription
          }
          sku
        }
        variation
        color
        quantity
        amountToCharge
        promoItem
      }
      subscription {
        isGift
        plan {
          _id
          title
          costWithCoupon
          actualCost
          originalCost
          actualCostAsShipping
        }
        babyBirthday
      }
      subCosts {
        subTotal
        giftCardDiscount
        couponDiscount
        shipping
        taxesAndFees
        overallTotal
        potentialGiftCardDiscount
        potentialTaxesAndFees
        potentialOverallTotal
        applicablePointsWithDiscount
        pointsDiscount
      }
      error {
        key
        value
      }
    }
  }
`;

const changeQuantityOfItemInCart = gql`
  mutation changeQuantityOfItemInCart(
    $customerIdForAdmin: String
    $productDetailsIdToChange: String!
    $newQuantity: Int!
  ) {
    changeQuantityOfItemInCart(
      customerIdForAdmin: $customerIdForAdmin
      productDetailsIdToChange: $productDetailsIdToChange
      newQuantity: $newQuantity
    ) {
      _id
      numberOfItems
      hasSubscription
      productsAndDetails {
        _id
        product {
          _id
          name
          brand
          actualCost
          media {
            color
            url
          }
          quantityDetails {
            variation
            color
            quantity
          }
          ownPromoData {
            isSubPromo
            isProductPromo
            isEverythingPromo
            isPostPurchasePromo
            isPreCheckoutPromo
            isVitaminPromo
            expiresOn
            validOn
            isAddOn
            isRecurringAddOn
            cost
            header
            subHeader
            buttonText
            promoDescription
          }
        }
        variation
        color
        quantity
        amountToCharge
      }
      subscription {
        isGift
        plan {
          _id
          title
          costWithCoupon
          actualCost
          originalCost
          actualCostAsShipping
        }
        babyBirthday
      }
      subCosts {
        subTotal
        giftCardDiscount
        couponDiscount
        shipping
        taxesAndFees
        overallTotal
        potentialGiftCardDiscount
        potentialTaxesAndFees
        potentialOverallTotal
        applicablePointsWithDiscount
        pointsDiscount
      }
      error {
        key
        value
      }
    }
  }
`;

const removeSubscriptionFromCart = gql`
  mutation removeSubscriptionFromCart($customerIdForAdmin: String) {
    removeSubscriptionFromCart(customerIdForAdmin: $customerIdForAdmin) {
      _id
      numberOfItems
      hasSubscription
      subscription {
        isGift
        plan {
          _id
          title
          costWithCoupon
          actualCost
          originalCost
        }
        babyBirthday
      }
      productsAndDetails {
        _id
        product {
          _id
          name
          brand
          actualCost
          urlName
          media {
            color
            url
          }
          quantityDetails {
            variation
            color
            quantity
          }
          ownPromoData {
            isSubPromo
            isProductPromo
            isEverythingPromo
            isPostPurchasePromo
            isPreCheckoutPromo
            isVitaminPromo
            expiresOn
            validOn
            isAddOn
            isRecurringAddOn
            cost
            header
            subHeader
            buttonText
            promoDescription
          }
          sku
        }
        variation
        color
        quantity
        amountToCharge
      }
      firstBoxShipping {
        _id
        title
        cost
        isInternational
        timeToShipInBusinessDays
      }
      coupon {
        _id
        code
        discountPercentage
        discountAmount
        freeShipping
      }
      subCosts {
        subTotal
        giftCardDiscount
        couponDiscount
        shipping
        taxesAndFees
        overallTotal
        potentialGiftCardDiscount
        potentialTaxesAndFees
        potentialOverallTotal
        applicablePointsWithDiscount
        pointsDiscount
      }
      error {
        key
        value
      }
    }
  }
`;

const updatePointsAppliedStatus = gql`
  mutation updatePointsAppliedStatus(
    $customerIdForAdmin: String
    $hasPointsApplied: Boolean!
  ) {
    updatePointsAppliedStatus(
      customerIdForAdmin: $customerIdForAdmin
      hasPointsApplied: $hasPointsApplied
    ) {
      _id
      hasPointsApplied
      firstBoxShipping {
        _id
        cost
        isInternational
        timeToShipInBusinessDays
        title
      }
      subCosts {
        subTotal
        giftCardDiscount
        couponDiscount
        shipping
        taxesAndFees
        overallTotal
        potentialGiftCardDiscount
        potentialTaxesAndFees
        potentialOverallTotal
        applicablePointsWithDiscount
        pointsDiscount
      }
    }
  }
`;

const cancelSubscription = gql`
  mutation cancelSubscription(
    $subscriptionId: String!
    $cancellationReason: String!
    $customerIdForAdmin: String
  ) {
    cancelSubscription(
      subscriptionId: $subscriptionId
      cancellationReason: $cancellationReason
      customerIdForAdmin: $customerIdForAdmin
    ) {
      _id
      status
      error {
        key
        value
      }
    }
  }
`;

const updateRenewalCouponAsUser = gql`
  mutation updateRenewalCouponAsUser(
    $subscriptionId: String!
    $renewalCouponCode: String!
  ) {
    updateRenewalCouponAsUser(
      subscriptionId: $subscriptionId
      renewalCouponCode: $renewalCouponCode
    ) {
      _id
      error {
        key
        value
      }
    }
  }
`;

const validateCouponsInCart = gql`
  mutation validateCouponsInCart($customerIdForAdmin: String) {
    validateCouponsInCart(customerIdForAdmin: $customerIdForAdmin) {
      _id
      coupon {
        _id
        code
        discountPercentage
        discountAmount
        freeShipping
      }
      subCosts {
        subTotal
        giftCardDiscount
        couponDiscount
        shipping
        taxesAndFees
        overallTotal
        potentialGiftCardDiscount
        potentialTaxesAndFees
        potentialOverallTotal
        applicablePointsWithDiscount
        pointsDiscount
      }
      error {
        key
        value
      }
    }
  }
`;

const validateProductsInCart = gql`
  mutation validateProductsInCart($customerIdForAdmin: String) {
    validateProductsInCart(customerIdForAdmin: $customerIdForAdmin) {
      _id
      numberOfItems
      productsAndDetails {
        _id
        product {
          _id
          name
          brand
          actualCost
          urlName
          media {
            color
            url
          }
          quantityDetails {
            variation
            color
            quantity
          }
          ownPromoData {
            isSubPromo
            isProductPromo
            isEverythingPromo
            isPostPurchasePromo
            isPreCheckoutPromo
            isVitaminPromo
            expiresOn
            validOn
            isAddOn
            isRecurringAddOn
            cost
            header
            subHeader
            buttonText
            promoDescription
          }
          sku
        }
        variation
        color
        quantity
        amountToCharge
      }
      subCosts {
        subTotal
        giftCardDiscount
        couponDiscount
        shipping
        taxesAndFees
        overallTotal
        potentialGiftCardDiscount
        potentialTaxesAndFees
        potentialOverallTotal
        applicablePointsWithDiscount
        pointsDiscount
      }
      error {
        key
        value
      }
    }
  }
`;

const sendAndStoreOffersSelected = gql`
  mutation sendAndStoreOffersSelected(
    $offerId: String!
    $questionResponses: [questionResponsesObject]
    $birthDay: String
    $birthMonth: String
    $birthYear: String
    $customerIdForAdmin: String
    $trustedFormUrl: String
    $orderConfirmationCode: String
  ) {
    sendAndStoreOffersSelected(
      offerId: $offerId
      questionResponses: $questionResponses
      birthDay: $birthDay
      birthMonth: $birthMonth
      birthYear: $birthYear
      customerIdForAdmin: $customerIdForAdmin
      trustedFormUrl: $trustedFormUrl
      orderConfirmationCode: $orderConfirmationCode
    ) {
      _id
    }
  }
`;

const addAddressToUser = gql`
  mutation addAddressToUser(
    $customerIdForAdmin: String
    $firstName: String!
    $lastName: String!
    $streetAddress: String!
    $aptDetails: String
    $city: String!
    $state: String!
    $zipCode: String!
    $country: String!
    $addressType: String
    $orderConfirmationCode: String
  ) {
    addAddressToUser(
      customerIdForAdmin: $customerIdForAdmin
      firstName: $firstName
      lastName: $lastName
      streetAddress: $streetAddress
      aptDetails: $aptDetails
      city: $city
      state: $state
      zipCode: $zipCode
      country: $country
      addressType: $addressType
      orderConfirmationCode: $orderConfirmationCode
    ) {
      _id
      isSubscribedToNewsletter
      email
      name {
        first
        last
      }
      providers
      isVerified
      token
      error {
        key
        value
      }
    }
  }
`;

const addInsuranceDataAndCreatePurchase = gql`
  mutation addInsuranceDataAndCreatePurchase(
    $insuranceType: String!
    $memberId: String!
    $dateOfBirth: String!
    $obgynName: String!
    $obgynPhoneNumber: String!
    $breastPump: String!
    $customerIdForAdmin: String
    $orderConfirmationCode: String
  ) {
    addInsuranceDataAndCreatePurchase(
      insuranceType: $insuranceType
      memberId: $memberId
      dateOfBirth: $dateOfBirth
      obgynName: $obgynName
      obgynPhoneNumber: $obgynPhoneNumber
      breastPump: $breastPump
      customerIdForAdmin: $customerIdForAdmin
      orderConfirmationCode: $orderConfirmationCode
    ) {
      _id
      isSubscribedToNewsletter
      email
      name {
        first
        last
      }
      isVerified
      token
      error {
        key
        value
      }
    }
  }
`;

const addPartnerClickToUser = gql`
  mutation addPartnerClickToUser(
    $customerIdForAdmin: String
    $partner: String!
  ) {
    addPartnerClickToUser(
      customerIdForAdmin: $customerIdForAdmin
      partner: $partner
    ) {
      _id
      isSubscribedToNewsletter
      email
      name {
        first
        last
      }
      isVerified
      token
      error {
        key
        value
      }
    }
  }
`;

const clearCart = gql`
  mutation clearCart {
    clearCart {
      _id
      numberOfItems
    }
  }
`;

const verifyCorporateAccount = gql`
  mutation verifyCorporateAccount($corporateVerificationCode: String!) {
    verifyCorporateAccount(
      corporateVerificationCode: $corporateVerificationCode
    ) {
      _id
      accountType
      activeCompanies
      corpRedirect
      token
      error {
        key
        value
      }
    }
  }
`;

const verifyInsuranceProviderAndAddDataToUser = gql`
  mutation verifyInsuranceProviderAndAddDataToUser(
    $customerIdForAdmin: String
    $provider: String!
    $insuranceType: String!
    $memberId: String!
    $dateOfBirth: String!
    $obgynName: String!
    $obgynPhoneNumber: String!
    $isOtherInsurance: Boolean
    $matchedPartner: String
    $orderConfirmationCode: String
    $isAgreedToMomEssentials: Boolean!
  ) {
    verifyInsuranceProviderAndAddDataToUser(
      customerIdForAdmin: $customerIdForAdmin
      provider: $provider
      insuranceType: $insuranceType
      memberId: $memberId
      dateOfBirth: $dateOfBirth
      obgynName: $obgynName
      obgynPhoneNumber: $obgynPhoneNumber
      isOtherInsurance: $isOtherInsurance
      matchedPartner: $matchedPartner
      orderConfirmationCode: $orderConfirmationCode
      isAgreedToMomEssentials: $isAgreedToMomEssentials
    ) {
      name
      urlName
      isOnBumpBoxes
      isPartnerApiEnabled
    }
  }
`;

const addOrRemoveProductToAddOns = gql`
  mutation addOrRemoveProductToAddOns(
    $customerIdForAdmin: String
    $subscriptionId: String!
    $productId: String!
    $productAlreadyAdded: Boolean!
    $isRecurringAddOn: Boolean
  ) {
    addOrRemoveProductToAddOns(
      customerIdForAdmin: $customerIdForAdmin
      subscriptionId: $subscriptionId
      productId: $productId
      productAlreadyAdded: $productAlreadyAdded
      isRecurringAddOn: $isRecurringAddOn
    ) {
      _id
      status
      renewOn
      recurringAddOns {
        amountToCharge
        product {
          _id
          name
          originalCost
          actualCost
          ownPromoData {
            isSubPromo
            isProductPromo
            isEverythingPromo
            isPostPurchasePromo
            isPreCheckoutPromo
            isVitaminPromo
            expiresOn
            validOn
            isAddOn
            isRecurringAddOn
            cost
            header
            subHeader
            buttonText
            promoDescription
          }
        }
      }
      addOnsWithDetails {
        _id
        quantity
        replacementItem
        color
        variation
        amountToCharge
        product {
          _id
          name
          actualCost
          originalCost
        }
      }
    }
  }
`;

const createRegistry = gql`
  mutation createRegistry(
    $shippingAddress: address!
    $dueDate: String!
    $gender: String!
    $displayGender: Boolean!
    $displayDueDate: Boolean!
    $subscriptionId: String!
    $customerIdForAdmin: String
  ) {
    createRegistry(
      shippingAddress: $shippingAddress
      dueDate: $dueDate
      gender: $gender
      displayGender: $displayGender
      displayDueDate: $displayDueDate
      subscriptionId: $subscriptionId
      customerIdForAdmin: $customerIdForAdmin
    ) {
      _id
      shareCode
      userEmail
    }
  }
`;

const editRegistry = gql`
  mutation editRegistry(
    $registryId: String!
    $registryFirstName: String!
    $registryLastName: String!
    $shippingAddress: address!
    $dueDate: String!
    $gender: String!
    $displayGender: Boolean!
    $displayDueDate: Boolean!
    $customerIdForAdmin: String
  ) {
    editRegistry(
      registryId: $registryId
      shippingAddress: $shippingAddress
      dueDate: $dueDate
      gender: $gender
      displayGender: $displayGender
      displayDueDate: $displayDueDate
      registryFirstName: $registryFirstName
      registryLastName: $registryLastName
      customerIdForAdmin: $customerIdForAdmin
    ) {
      _id
      name {
        first
        last
      }
      subscription {
        babyGender
        babyBirthday
        shippingAddress {
          name {
            first
            last
          }
          streetAddress
          aptDetails
          city
          state
          zipCode
          country
        }
      }
      sponsors {
        name {
          first
          last
        }
        email
        numOfBoxesSponsored
        giftMessage
        thankYouCreatedAt
        isAnonymous
        createdAt
      }
      displayGender
      displayDueDate
      numOfBoxesSponsored
      daysToGo
      registryProgress
      shareCode
    }
  }
`;

const addOrRemoveShippingInsuranceForCart = gql`
  mutation addOrRemoveShippingInsuranceForCart(
    $cartId: String!
    $isAddingInsurance: Boolean!
  ) {
    addOrRemoveShippingInsuranceForCart(
      cartId: $cartId
      isAddingInsurance: $isAddingInsurance
    ) {
      _id
      shippingInsurance {
        cost
      }
    }
  }
`;

const addOrRemoveShippingInsuranceForSubscription = gql`
  mutation addOrRemoveShippingInsuranceForSubscription(
    $subscriptionId: String!
    $isAddingInsurance: Boolean!
  ) {
    addOrRemoveShippingInsuranceForSubscription(
      subscriptionId: $subscriptionId
      isAddingInsurance: $isAddingInsurance
    ) {
      _id
      shippingInsurance {
        cost
      }
    }
  }
`;

const addAddressToRegistryCart = gql`
  mutation addAddressToRegistryCart(
    $customerIdForAdmin: String
    $registryId: String!
  ) {
    addAddressToRegistryCart(
      customerIdForAdmin: $customerIdForAdmin
      registryId: $registryId
    ) {
      _id
    }
  }
`;

const storeAndSendSMSOptIn = gql`
  mutation storeAndSendSMSOptIn(
    $customerIdForAdmin: String
    $attentiveVisitorId: String
    $isOptedInSMS: Boolean!
    $email: String
    $phoneNumber: String
  ) {
    storeAndSendSMSOptIn(
      customerIdForAdmin: $customerIdForAdmin
      attentiveVisitorId: $attentiveVisitorId
      isOptedInSMS: $isOptedInSMS
      email: $email
      phoneNumber: $phoneNumber
    ) {
      _id
    }
  }
`;

const getPreCheckoutPromoProduct = gql`
  query getPreCheckoutPromoProduct(
    $isRecurringAddOnPromo: Boolean
    $provider: String
    $productAddedToCart: String
  ) {
    getPreCheckoutPromoProduct(
      isRecurringAddOnPromo: $isRecurringAddOnPromo
      provider: $provider
      productAddedToCart: $productAddedToCart
    ) {
      _id
      name
      isActive
      description
      ingredients
      originalCost
      actualCost
      urlName
      averageRating
      quantityDictionaryAsString
      totalQuantity
      variationName
      showColorsAsMultipleItems
      showVariationsAsMultipleItems
      isDropShip
      sku
      isComposite
      brand
      media {
        color
        url
        variation
        altTag
      }
      tags {
        _id
        description
        isInternal
        bitsyParent {
          _id
          description
        }
        bumpParent {
          _id
          description
        }
      }
      ownPromoData {
        isSubPromo
        isProductPromo
        isEverythingPromo
        isPostPurchasePromo
        isPreCheckoutPromo
        isVitaminPromo
        expiresOn
        validOn
        isAddOn
        isRecurringAddOn
        cost
        header
        subHeader
        buttonText
        promoDescription
      }
      subscriptionPlan {
        _id
        provider
        months
        title
        orderToShow
        costWithCoupon
        actualCost
        originalCost
        tagType
        isHidden
        minimumMonths
        actualCostAsShipping
        couponToAutoPopulate {
          _id
          code
          discountPercentage
          discountAmount
          freeShipping
          addedProducts {
            _id
            name
            description
            descriptionComponents
            ingredients
            colors
            variations
            originalCost
            actualCost
            urlName
            brand
            media {
              url
            }
          }
        }
        bulletPoints {
          point1
          point2
          point3
          point4
        }
      }
      discounted
      totalNumberOfProducts
      __typename @skip(if: true)
    }
  }
`;

const checkIfSubscriptionExists = gql`
  query checkIfSubscriptionExists($confirmationCode: String!) {
    checkIfSubscriptionExists(confirmationCode: $confirmationCode) {
      _id
    }
  }
`;

const getReferralCodeForNewOrReturningUser = gql`
  mutation getReferralCodeForNewOrReturningUser(
    $name: String!
    $email: String!
  ) {
    getReferralCodeForNewOrReturningUser(name: $name, email: $email) {
      _id
      referralCode
      email
      name {
        first
        last
      }
    }
  }
`;

const getReferralCodeForLoggedInUser = gql`
  mutation getReferralCodeForLoggedInUser($customerIdForAdmin: String) {
    getReferralCodeForLoggedInUser(customerIdForAdmin: $customerIdForAdmin) {
      _id
      referralCode
      email
      name {
        first
        last
      }
    }
  }
`;

export default {
  queries: {
    getSubscriptionQuery,
    productQuery,
    userInfoQuery,
    allPlansQuery,
    plansForProviderQuery,
    allShippingOptionsQuery,
    orderInfoQuery,
    purchaseInfoQuery,
    subscriptionInProgressQuery,
    getProductsQuery,
    cartItemCountQuery,
    availablePointsQuery,
    getCurrentShopTag,
    allPressReleasesQuery,
    getPromoProductsForCart,
    getPromoProductsForPostPurchase,
    afterPurchaseOffers,
    getBreastPumpProducts,
    getCompanyByCode,
    getCorporateProducts,
    validateCompanySignUpUrl,
    addEmailToMasterList,
    sendDueDateCalculatorKlaviyoEvents,
    addEmailToBreastPumpMasterList,
    addEmailToGiveawayList,
    sendLifeInsuranceQuoteEmail,
    getSubscriptionAddOnProducts,
    getRegistryInfo,
    getRegistryInfoForOwner,
    searchForRegistries,
    getRegistryProduct,
    validateRegistryAddress,
    getPreCheckoutPromoProduct,
    getSubscriptionAddOnProductsForLandingPage,
    checkIfSubscriptionExists,
    refreshUserToken,
  },
  mutations: {
    signInUser,
    signUpUser,
    signOutUser,
    confirmUserPassword,
    addDiscountToCart,
    removeDiscountFromCart,
    editAccountDetails,
    editShippingAddress,
    editPaymentMethod,
    editBabyDetails,
    sendForgotPasswordLink,
    resetPassword,
    skipNextMonth,
    sendCorporateGiftingContactEmail,
    sendEmailToHelloBitsyBoxesWithMessage,
    addReview,
    editReview,
    deleteReview,
    addAllShippingData,
    addSubscriptionDetails,
    reactivateSubscription,
    extendSubscription,
    addItemToCart,
    purchaseCart,
    removeItemFromCart,
    changeQuantityOfItemInCart,
    removeSubscriptionFromCart,
    getEmptyUser,
    addGuestCheckoutData,
    updatePointsAppliedStatus,
    unskipNextMonth,
    cancelSubscription,
    updateRenewalCouponAsUser,
    validateCouponsInCart,
    validateProductsInCart,
    sendAndStoreOffersSelected,
    addAddressToUser,
    verifyInsuranceProviderAndAddDataToUser,
    addInsuranceDataAndCreatePurchase,
    addPartnerClickToUser,
    clearCart,
    verifyCorporateAccount,
    addOrRemoveProductToAddOns,
    createRegistry,
    editRegistry,
    addOrRemoveShippingInsuranceForCart,
    addOrRemoveShippingInsuranceForSubscription,
    addAddressToRegistryCart,
    storeAndSendSMSOptIn,
    getReferralCodeForNewOrReturningUser,
    getReferralCodeForLoggedInUser,
    cartInfo,
  },
};
